import { useEffect, useState } from 'react';

const ROI_DEFAULTS = {
  reportsPerMonth: 2,
  hoursWithoutTool: 6,
  hoursWithTool: 1,
  hourlyRate: 100,
};

export const formatEuro = (n: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

// Nur für die Dashboard-Kachel (useRoiCalculator) relevant — die Werte sind eine persönliche
// Annahme des Beraters, kein geteilter Datensatz, daher reicht localStorage statt Backend.
// Preise.tsx (RoiCalculator-Komponente unten) ist die anonyme Marketing-Variante ohne Login,
// bekommt bewusst keine Persistenz.
const ROI_STORAGE_KEY = 'startklar.roiCalc.v1';

function loadRoiStorage(): Partial<typeof ROI_DEFAULTS> {
  try {
    const raw = localStorage.getItem(ROI_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Reine Rechenlogik, getrennt von der Darstellung — wiederverwendet von RoiCalculator
 * (volle Ansicht, Preise.tsx) und der kompakten Dashboard-Kachel (RoiStatCard). */
export function useRoiCalculator() {
  const [roiReports, setRoiReports] = useState(() => loadRoiStorage().reportsPerMonth ?? ROI_DEFAULTS.reportsPerMonth);
  const [roiHoursWithout, setRoiHoursWithout] = useState(() => loadRoiStorage().hoursWithoutTool ?? ROI_DEFAULTS.hoursWithoutTool);
  const [roiHoursWith, setRoiHoursWith] = useState(() => loadRoiStorage().hoursWithTool ?? ROI_DEFAULTS.hoursWithTool);
  const [roiRate, setRoiRate] = useState(() => loadRoiStorage().hourlyRate ?? ROI_DEFAULTS.hourlyRate);

  // Kundenfeedback 16.10.2026: eingegebene Werte waren nach Reload/Navigation wieder weg —
  // useState allein hielt sie nur im Speicher dieses einen Renders, ohne jede Persistenz.
  useEffect(() => {
    try {
      localStorage.setItem(ROI_STORAGE_KEY, JSON.stringify({
        reportsPerMonth: roiReports,
        hoursWithoutTool: roiHoursWithout,
        hoursWithTool: roiHoursWith,
        hourlyRate: roiRate,
      }));
    } catch {
      // Private-Modus/blockierter Storage — Werte bleiben dann nur für diese Sitzung erhalten.
    }
  }, [roiReports, roiHoursWithout, roiHoursWith, roiRate]);

  const hoursSavedPerReport = Math.max(0, roiHoursWithout - roiHoursWith);
  const hoursSavedPerMonth = hoursSavedPerReport * roiReports;
  const moneySavedPerYear = hoursSavedPerMonth * 12 * roiRate;
  const moneySavedPerReport = hoursSavedPerReport * roiRate;

  return {
    roiReports, setRoiReports,
    roiHoursWithout, setRoiHoursWithout,
    roiHoursWith, setRoiHoursWith,
    roiRate, setRoiRate,
    hoursSavedPerReport, hoursSavedPerMonth, moneySavedPerYear, moneySavedPerReport,
  };
}

export default function RoiCalculator({
  darkMode,
  variant = 'plain',
}: {
  darkMode: boolean;
  variant?: 'glass' | 'plain';
}) {
  const [roiReports, setRoiReports] = useState(ROI_DEFAULTS.reportsPerMonth);
  const [roiHoursWithout, setRoiHoursWithout] = useState(ROI_DEFAULTS.hoursWithoutTool);
  const [roiHoursWith, setRoiHoursWith] = useState(ROI_DEFAULTS.hoursWithTool);
  const [roiRate, setRoiRate] = useState(ROI_DEFAULTS.hourlyRate);

  const hoursSavedPerReport = Math.max(0, roiHoursWithout - roiHoursWith);
  const hoursSavedPerMonth = hoursSavedPerReport * roiReports;
  const moneySavedPerYear = hoursSavedPerMonth * 12 * roiRate;
  const moneySavedPerReport = hoursSavedPerReport * roiRate;

  const formatEuro = (n: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

  const muted = darkMode ? 'text-slate-300/85' : 'text-slate-600';
  const heading = darkMode ? 'text-white' : 'text-slate-900';

  const cardClass = variant === 'glass'
    ? 'lg-card lg-surface p-6 md:p-8'
    : `rounded-xl border shadow-sm p-6 md:p-8 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const spinnerOff = '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';
  const fieldClass = variant === 'glass'
    ? `lg-field w-full px-3 py-2.5 text-base font-semibold ${spinnerOff} ${darkMode ? 'text-white' : 'text-slate-900'}`
    : `w-full px-3 py-2.5 rounded-lg border text-base font-semibold ${spinnerOff} ${darkMode ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`;
  const resultClass = variant === 'glass'
    ? 'lg-surface p-4 text-center'
    : `rounded-lg p-4 text-center ${darkMode ? 'bg-slate-700/40' : 'bg-slate-50'}`;

  return (
    <div className={cardClass}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Berichte pro Monat', value: roiReports, setter: setRoiReports, min: 1, max: 20, step: 1, suffix: '' },
          { label: 'Stunden ohne Tool', value: roiHoursWithout, setter: setRoiHoursWithout, min: 1, max: 40, step: 0.5, suffix: 'h' },
          { label: 'Stunden mit Tool', value: roiHoursWith, setter: setRoiHoursWith, min: 0, max: 40, step: 0.25, suffix: 'h' },
          { label: 'Stundensatz (netto)', value: roiRate, setter: setRoiRate, min: 0, max: 500, step: 5, suffix: '€' },
        ].map((field) => (
          <label key={field.label} className="flex flex-col gap-1">
            <span className={`text-xs font-medium ${muted}`}>{field.label}</span>
            <div className="relative">
              <input
                type="number"
                min={field.min}
                max={field.max}
                step={field.step}
                value={field.value}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  field.setter(Number.isFinite(n) && n >= 0 ? n : 0);
                }}
                className={`${fieldClass} ${field.suffix ? 'pr-8' : ''}`}
              />
              {field.suffix && (
                <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${muted}`}>{field.suffix}</span>
              )}
            </div>
          </label>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={resultClass}>
          <div className={`text-xs uppercase tracking-wide ${muted}`}>Zeit gespart / Monat</div>
          <div className={`text-3xl font-bold mt-1 ${heading}`}>{hoursSavedPerMonth} h</div>
          <div className={`text-xs mt-1 ${muted}`}>{hoursSavedPerReport} h pro Bericht</div>
        </div>
        <div className={resultClass}>
          <div className={`text-xs uppercase tracking-wide ${muted}`}>Geld gespart / Jahr</div>
          <div className="text-3xl font-bold mt-1 text-emerald-500">{formatEuro(moneySavedPerYear)}</div>
          <div className={`text-xs mt-1 ${muted}`}>{formatEuro(moneySavedPerReport)} pro Bericht</div>
        </div>
      </div>
    </div>
  );
}
